import Select from 'src/components/primitives/Select/Select';
import { Settings } from 'src/components/Settings/SettingsForm';
import {
	SubscriberType,
	SelfEventSubscriberType,
	GlobalEventSubscriberType,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariableTypes';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import EVENTS_SUBSCRIPTION_UTILS from 'src/utils/EventsSubscription/eventsSubscriptionUtils';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';

const ROW_CLASSNAME =
	'flex flex-col gap-y-sm sm:flex-row items-start sm:items-center justify-between w-full text-text-default typo-b4';

const ACCOUNT_SUBSCRIPTION_TYPE_OPTIONS: {
	label: string;
	value: SubscriberType;
}[] = [
	{
		label: 'Websocket',
		value: 'websocket',
	},
	{
		label: 'Polling',
		value: 'polling',
	},
];

const SELF_EVENTS_SUBSCRIPTION_OPTIONS: {
	label: string;
	value: SelfEventSubscriberType;
}[] = [
	{
		label: 'Events Server (EventSubscriber)',
		value: 'events-server',
	},
	{
		label: 'Websocket (EventSubscriber)',
		value: 'websocket',
	},
	{
		label: 'Polling (EventSubscriber)',
		value: 'polling',
	},
];

const GLOBAL_EVENTS_SUBSCRIPTION_OPTIONS: {
	label: string;
	value: GlobalEventSubscriberType | 'default';
}[] = [
	{
		label: 'Default',
		value: 'default',
	},
	{
		value: 'data-api-trade-publisher',
		label: 'Data API',
	},
	{
		value: 'dlob-server-trade-publisher',
		label: 'DLOB Server',
	},
	{
		value: 'events-server',
		label: 'Events Server (EventSubscriber)',
	},
	{
		value: 'websocket',
		label: 'Websocket (EventSubscriber)',
	},
	{
		value: 'polling',
		label: 'Polling (EventSubscriber)',
	},
];

interface Props {
	localSettings: Settings;
	updateSettingsValue: (update: Partial<UserSettings>) => void;
}

export default function NetworkSettingsDevInputs({
	localSettings,
	updateSettingsValue,
}: Props) {
	const {
		selectOptions: accountSubscriptionTypeOptions,
		selectedOption: accountSubscriptionTypeSelectedOption,
		value: accountSubscriptionTypeValue,
		onValueChange: accountSubscriptionTypeValueChange,
		open: accountSubscriptionTypeOpen,
		onOpenChange: accountSubscriptionTypeOpenChange,
	} = useSelect({
		selectedValue: localSettings.accountSubscriberType,
		onSelectedValueChange: (value) => {
			updateSettingsValue({
				accountSubscriberType: value,
			});
		},
		options: ACCOUNT_SUBSCRIPTION_TYPE_OPTIONS,
	});

	const {
		selectOptions: globalEventsSubscriptionTypeOptions,
		selectedOption: globalEventsSubscriptionTypeSelectedOption,
		value: globalEventsSubscriptionTypeValue,
		onValueChange: globalEventsSubscriptionTypeValueChange,
		open: globalEventsSubscriptionTypeOpen,
		onOpenChange: globalEventsSubscriptionTypeOpenChange,
	} = useSelect({
		selectedValue: localSettings.globalEventsSubscriptionType,
		onSelectedValueChange: (value) => {
			updateSettingsValue({
				globalEventsSubscriptionType: value !== 'default' ? value : undefined,
			});
		},
		options: GLOBAL_EVENTS_SUBSCRIPTION_OPTIONS,
	});

	const {
		selectOptions: selfEventsSubscriptionTypeOptions,
		selectedOption: selfEventsSubscriptionTypeSelectedOption,
		value: selfEventsSubscriptionTypeValue,
		onValueChange: selfEventsSubscriptionTypeValueChange,
		open: selfEventsSubscriptionTypeOpen,
		onOpenChange: selfEventsSubscriptionTypeOpenChange,
	} = useSelect({
		selectedValue: localSettings.selfEventsSubscriptionType,
		onSelectedValueChange: (value) => {
			updateSettingsValue({
				selfEventsSubscriptionType: value,
			});
		},
		options: SELF_EVENTS_SUBSCRIPTION_OPTIONS,
	});

	return (
		<>
			<div className={ROW_CLASSNAME}>
				<DefinitionTooltip definitionId="settingsAccountSubscriptionType">
					Account Subscription Type
				</DefinitionTooltip>
				<Select.Root
					open={accountSubscriptionTypeOpen}
					onOpenChange={accountSubscriptionTypeOpenChange}
					value={accountSubscriptionTypeValue}
					onValueChange={accountSubscriptionTypeValueChange}
				>
					<Select.Trigger>
						{accountSubscriptionTypeSelectedOption?.label}
					</Select.Trigger>
					<Select.Options>
						{accountSubscriptionTypeOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			</div>

			<div className={ROW_CLASSNAME}>
				<DefinitionTooltip definitionId="settingsGlobalEventsSubscription">
					Global Events Subscription
				</DefinitionTooltip>
				<Select.Root
					open={globalEventsSubscriptionTypeOpen}
					onOpenChange={globalEventsSubscriptionTypeOpenChange}
					value={globalEventsSubscriptionTypeValue}
					onValueChange={globalEventsSubscriptionTypeValueChange}
				>
					<Select.Trigger>
						{globalEventsSubscriptionTypeSelectedOption?.label ??
							`Default (${EVENTS_SUBSCRIPTION_UTILS.getPreferredGlobalEventSubscriptionType(
								{ ignoreUserSettings: true }
							)})`}
					</Select.Trigger>
					<Select.Options>
						{globalEventsSubscriptionTypeOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			</div>

			<div className={ROW_CLASSNAME}>
				<DefinitionTooltip definitionId="settingsUserEventsSubscription">
					User Events Subscription
				</DefinitionTooltip>
				<Select.Root
					open={selfEventsSubscriptionTypeOpen}
					onOpenChange={selfEventsSubscriptionTypeOpenChange}
					value={selfEventsSubscriptionTypeValue}
					onValueChange={selfEventsSubscriptionTypeValueChange}
				>
					<Select.Trigger>
						{selfEventsSubscriptionTypeSelectedOption?.label ??
							`Default (${EVENTS_SUBSCRIPTION_UTILS.getPreferredSelfSubscriptionType(
								{ ignoreUserSettings: true }
							)})`}
					</Select.Trigger>
					<Select.Options>
						{selfEventsSubscriptionTypeOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			</div>
		</>
	);
}
